// Searchable integrator combobox. Scales beyond the native <select>'s limits
// when an operator runs dozens or hundreds of tenants. Type-ahead filter,
// keyboard navigation, recents pinned at the top.
//
// Backwards-compatible with the rest of the app: a hidden <select> mirrors
// the chosen value and fires a `change` event so existing listeners (route
// rewrite, approvals badge, topbar badges) keep working unchanged.

import type { IntegratorSummary } from '@apx/admin-sdk';
import { icon } from './icons.js';

const RECENT_KEY = 'apx-admin:integrators:recent';
const MAX_RECENT = 5;

export interface IntegratorPickerHandle {
  /** Mirror element — read/write `.value` like a <select>. */
  select: HTMLSelectElement;
  /** Replace the integrator list (called on bootstrap + on integrator CRUD). */
  setIntegrators(list: ReadonlyArray<IntegratorSummary>): void;
  /** Programmatic value set (does NOT fire `change`). */
  setValue(id: string): void;
  /** Force the dropdown closed. */
  close(): void;
  /** Set the trigger label to reflect the current scope from the route. */
  setContext(ctx: { mode: 'provider' } | { mode: 'integrator'; id: string }): void;
  /** Whether the pinned Provider entry is shown (RBAC-gated by the caller). */
  setProviderEntryVisible(visible: boolean): void;
}

export interface InstallPickerOptions {
  /** Fired when the user picks the pinned "Provider — all integrators" entry. */
  onSelectProvider: () => void;
}

function loadRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

function saveRecents(ids: ReadonlyArray<string>): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch {
    /* private mode — fine */
  }
}

export function pushRecentIntegrator(id: string): void {
  if (!id) return; // ignore provider scope (empty value) — not a recent tenant
  const cur = loadRecents().filter((x) => x !== id);
  cur.unshift(id);
  saveRecents(cur);
}

export function installIntegratorPicker(
  mount: HTMLElement,
  opts: InstallPickerOptions = { onSelectProvider: () => {} },
): IntegratorPickerHandle {
  // Hidden <select> keeps a `<select id="integrator-picker">` API for the rest
  // of the app. We never render it; we just write its `.value` and dispatch
  // `change` when the combobox selection moves.
  const select = document.createElement('select');
  select.id = 'integrator-picker';
  select.style.display = 'none';
  mount.appendChild(select);

  const root = document.createElement('div');
  root.className = 'tp';
  root.innerHTML = `
    <button type="button" class="tp-trigger" aria-haspopup="listbox" aria-expanded="false">
      <span class="tp-trigger-label">Select integrator…</span>
      <span class="tp-trigger-caret">${caretSvg()}</span>
    </button>
    <div class="tp-panel hidden" role="dialog">
      <div class="tp-search">
        <span class="tp-search-icon">${icon('search', { size: 14 })}</span>
        <input class="tp-search-input" type="search" placeholder="Search integrators…" autocomplete="off" />
      </div>
      <div class="tp-list" role="listbox" tabindex="-1"></div>
      <div class="tp-foot">
        <kbd>↑↓</kbd> navigate · <kbd>↵</kbd> select · <kbd>esc</kbd> close
      </div>
    </div>
  `;
  mount.appendChild(root);

  const triggerBtn = root.querySelector<HTMLButtonElement>('.tp-trigger')!;
  const triggerLabel = root.querySelector<HTMLElement>('.tp-trigger-label')!;
  const panel = root.querySelector<HTMLElement>('.tp-panel')!;
  const searchInput = root.querySelector<HTMLInputElement>('.tp-search-input')!;
  const listEl = root.querySelector<HTMLElement>('.tp-list')!;

  let tenants: ReadonlyArray<IntegratorSummary> = [];
  let filtered: IntegratorSummary[] = [];
  // -1 means "provider row active" when providerEntryVisible is true;
  // 0+ indexes into the filtered integrator array.
  let activeIdx = -1;
  let open = false;
  let providerEntryVisible = true;
  // Tracks whether the trigger label is owned by setContext(provider) or by
  // normal selection. updateTriggerLabel() is a no-op in provider mode so
  // setIntegrators / setValue / commit don't clobber the route-driven label.
  let contextMode: 'provider' | 'integrator' = 'integrator';

  function updateTriggerLabel(): void {
    const t = tenants.find((x) => x.id === select.value);
    if (contextMode === 'provider') {
      // Provider-context routes (e.g. game config) are tenant-selectable: the
      // label must follow the selected value — the picked integrator's name, or
      // the pinned "Provider · all integrators" when no tenant is selected.
      triggerLabel.textContent = t ? `${t.name}` : 'Provider · all integrators';
      triggerLabel.dataset.slug = t?.slug ?? '';
      return;
    }
    triggerLabel.textContent = t ? `${t.name}` : 'Select integrator…';
    triggerLabel.dataset.slug = t?.slug ?? '';
  }

  function getProviderRow(): HTMLElement | null {
    return listEl.querySelector<HTMLElement>('.tp-item-provider');
  }

  function paintActive(): void {
    // Clear active on integrator rows
    const rows = listEl.querySelectorAll<HTMLElement>('.tp-row');
    rows.forEach((r) => r.classList.remove('active'));

    // Clear active on provider row
    const provRow = getProviderRow();
    if (provRow) provRow.classList.remove('active');

    if (activeIdx === -1 && providerEntryVisible) {
      // Provider row is active
      if (provRow) {
        provRow.classList.add('active');
        provRow.scrollIntoView({ block: 'nearest' });
      }
    } else if (activeIdx >= 0 && activeIdx < filtered.length) {
      const target = listEl.querySelector<HTMLElement>(`.tp-row[data-i="${activeIdx}"]`);
      if (target) {
        target.classList.add('active');
        target.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  function renderList(): void {
    const query = searchInput.value.trim().toLowerCase();
    const matchFn = (t: IntegratorSummary): boolean =>
      !query || t.name.toLowerCase().includes(query) || t.slug.toLowerCase().includes(query);

    const matching = tenants.filter(matchFn);
    const recents = query ? [] : loadRecents()
      .map((id) => tenants.find((t) => t.id === id))
      .filter((t): t is IntegratorSummary => Boolean(t));

    filtered = [];
    const seen = new Set<string>();
    const html: string[] = [];

    if (recents.length > 0) {
      html.push('<div class="tp-section">Recent</div>');
      for (const t of recents) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        filtered.push(t);
        html.push(rowHtml(t, filtered.length - 1));
      }
      html.push('<div class="tp-section">All integrators</div>');
    }

    let added = 0;
    for (const t of matching) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      filtered.push(t);
      html.push(rowHtml(t, filtered.length - 1));
      added += 1;
    }

    if (filtered.length === 0) {
      html.push('<div class="tp-empty muted">No integrators match.</div>');
    } else if (added === 0 && recents.length > 0) {
      // recents covered everything; nothing to add
    }

    listEl.innerHTML = html.join('');

    if (providerEntryVisible) {
      const provSelected = select.value === '';
      const provRow = document.createElement('div');
      provRow.className = `tp-item-provider${provSelected ? ' selected' : ''}`;
      provRow.setAttribute('role', 'option');
      provRow.innerHTML = `
        <span class="tp-row-ic">${icon(provSelected ? 'check' : 'list', { size: 16 })}</span>
        <div class="tp-row-main">
          <div class="tp-row-name">All integrators</div>
          <div class="tp-row-slug">Provider-wide overview</div>
        </div>
        <span class="pill tp-row-status tp-scope-pill">provider</span>`;
      listEl.insertBefore(provRow, listEl.firstChild);
    }

    activeIdx = filtered.findIndex((t) => t.id === select.value);
    if (activeIdx < 0 && filtered.length > 0) activeIdx = 0;
    paintActive();
    bindRowClicks();
  }

  function rowHtml(t: IntegratorSummary, idx: number): string {
    const selected = t.id === select.value;
    const statusCls = t.status === 'active' ? 'live' : 'warn';
    return `<div class="tp-row${selected ? ' selected' : ''}" role="option" data-i="${idx}" data-id="${t.id}">
      <span class="tp-row-ic">${icon(selected ? 'check' : 'building', { size: 16 })}</span>
      <div class="tp-row-main">
        <div class="tp-row-name">${escapeHtml(t.name)}</div>
        <div class="tp-row-slug">${escapeHtml(t.slug)}</div>
      </div>
      <span class="pill ${statusCls} tp-row-status">${escapeHtml(t.status)}</span>
    </div>`;
  }

  function bindRowClicks(): void {
    // Integrator rows
    listEl.querySelectorAll<HTMLElement>('.tp-row').forEach((row) => {
      row.addEventListener('click', () => {
        const id = row.dataset.id;
        if (id) commit(id);
      });
    });

    // Provider row — detected by class so both the pinned row and any future
    // pinned entries follow the same wiring path as integrator rows.
    const provRow = getProviderRow();
    if (provRow) {
      provRow.addEventListener('click', () => {
        closePanel();
        opts.onSelectProvider();
      });
    }
  }

  function commit(id: string): void {
    if (!tenants.some((t) => t.id === id)) return;
    select.value = id;
    pushRecentIntegrator(id);
    updateTriggerLabel();
    closePanel();
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function openPanel(): void {
    if (open) return;
    open = true;
    panel.classList.remove('hidden');
    triggerBtn.setAttribute('aria-expanded', 'true');
    searchInput.value = '';
    renderList();
    setTimeout(() => searchInput.focus(), 0);
  }

  function closePanel(): void {
    if (!open) return;
    open = false;
    panel.classList.add('hidden');
    triggerBtn.setAttribute('aria-expanded', 'false');
  }

  triggerBtn.addEventListener('click', () => {
    if (open) closePanel();
    else openPanel();
  });

  searchInput.addEventListener('input', () => {
    renderList();
  });

  searchInput.addEventListener('keydown', (ev) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      if (providerEntryVisible && activeIdx === -1) {
        // Move from provider row down to first integrator row
        if (filtered.length > 0) activeIdx = 0;
        paintActive();
      } else if (filtered.length > 0) {
        activeIdx = (activeIdx + 1) % filtered.length;
        paintActive();
      }
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      if (providerEntryVisible && activeIdx === 0) {
        // Move from first integrator row up to provider row
        activeIdx = -1;
        paintActive();
      } else if (providerEntryVisible && activeIdx === -1) {
        // Already at provider row — stay (no wrap-around into integrators)
      } else if (filtered.length > 0) {
        activeIdx = (activeIdx - 1 + filtered.length) % filtered.length;
        paintActive();
      }
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      if (activeIdx === -1 && providerEntryVisible) {
        closePanel();
        opts.onSelectProvider();
      } else {
        const sel = filtered[activeIdx];
        if (sel) commit(sel.id);
      }
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      closePanel();
      triggerBtn.focus();
    }
  });

  // Click outside closes
  document.addEventListener('click', (ev) => {
    if (!open) return;
    if (!root.contains(ev.target as Node)) closePanel();
  });

  return {
    select,
    setIntegrators(list) {
      tenants = list;
      // Sync hidden <select> options so any external `.value` write
      // still validates (browsers ignore values not in the options list).
      select.innerHTML = '';
      for (const t of list) {
        const opt = document.createElement('option');
        opt.value = t.id;
        opt.textContent = `${t.name} (${t.slug})`;
        select.appendChild(opt);
      }
      updateTriggerLabel();
      if (open) renderList();
    },
    setValue(id) {
      select.value = id;
      updateTriggerLabel();
    },
    close: closePanel,
    setContext(ctx) {
      if (ctx.mode === 'provider') {
        contextMode = 'provider';
        // Value-aware: shows the selected integrator's name, or the pinned
        // "Provider · all integrators" when no tenant is selected.
        updateTriggerLabel();
        triggerBtn.classList.remove('tp-trigger-integrator');
      } else {
        contextMode = 'integrator';
        const t = tenants.find((x) => x.id === ctx.id);
        triggerLabel.textContent = t ? t.name : 'Select integrator…';
        triggerBtn.classList.add('tp-trigger-integrator');
      }
    },
    setProviderEntryVisible(visible) {
      providerEntryVisible = visible;
      if (open) renderList();
    },
  };
}

function caretSvg(): string {
  return '<svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
